















































import { defineComponent, ref, watch } from "@vue/composition-api";

import { Item } from "@/tools/msa/models/item";
import { ConceptType } from "@/utils/analysis";
import { waitTicks } from "@/tools/msa/utils/ticks";
import { Document, Session, Token } from "@/tools/msa/models";
import { mapItemsByTokens } from "@/tools/msa/utils/map";
import { findScrollableParent, isWithinView } from "@/tools/msa/utils/scroll";

import NavigablePane from "@/components/NavigablePane.vue";
import { useSelection } from "@/tools/msa/utils/selection";
import { encodeTextWithIcd } from "@/tools/msa/processing/icd";
import DocumentTabs from "@/tools/msa/components/DocumentTabs.vue";
import BasePage from "@/tools/msa/components/document/BasePage.vue";
import BaseToken from "@/tools/msa/components/document/BaseToken.vue";
import AnnotationButtons from "@/tools/msa/components/AnnotationButtons.vue";
import Bluebird from "bluebird";
import ZoomButtons from "@/tools/msa/components/ZoomButtons.vue";

interface Props {
  session: Session;
  document: Document;
  selected: Item | null;
}

const ICD_PATTERN = /(\d{3}\.?\d{0,2}|E\d{3}\.?\d?|V\d{2}\.?\d{0,2}|[A-TV-Z][0-9][0-9AB]\.?[0-9A-TV-Z]{0,4})/;

export default defineComponent<Props>({
  name: "DocumentView",
  components: {
    ZoomButtons,
    AnnotationButtons,
    NavigablePane,
    BasePage,
    DocumentTabs,
    BaseToken
  },
  props: {
    document: { type: Object, required: false },
    session: { type: Object, required: false },
    selected: { type: Object, default: () => null }
  },
  setup(props: Props, context) {
    const fontSize = ref<number>(100);

    const scrollable = ref<NavigablePane | null>(null);

    const itemsByToken = ref<Map<Token, Item[]>>(new Map());

    const { selection, range, reset: resetSelection } = useSelection(
      props,
      scrollable
    );

    watch(
      () => props.selected,
      async () => {
        if (props.selected === null) return;
        await waitTicks(5);
        const elements = Array.from<HTMLElement>(
          document.querySelectorAll(".evidence-highlighted")
        );

        if (elements.length === 0) return;
        const container = findScrollableParent(elements[0], 5);

        if (elements.some(element => isWithinView(element, container))) return;
        let current = elements[0],
          sum = 0;

        while (current.parentElement !== container) {
          sum += current.offsetTop;
          current = current.parentElement!;
        }

        container.scrollTop = sum - 50;
      }
    );

    watch(
      [() => props.document, () => props.document.items],
      () =>
        (itemsByToken.value = mapItemsByTokens(props.document, [
          ConceptType.Disease,
          ConceptType.Drug,
          ConceptType.Device
        ])),
      { immediate: true }
    );

    watch(
      () => props.document,
      () => {
        if (scrollable.value) scrollable.value.$el.scrollTop = 0;
      }
    );

    const annotate = async (
      document: Document,
      input: string,
      key: "diagnoses" | "assessments" | "medications" | "plans"
    ) => {
      if (input.length === 0) return;
      let text = input;

      if (key === "diagnoses") {
        text = (await Bluebird.map(
          input
            .split(/(?=\n#|\s\d{1,2}[.)]?(?!\d))/)
            .map(term => term.replace(/\n/g, " ").trim()),
          async term =>
            [
              term,
              !ICD_PATTERN.test(term)
                ? await encodeTextWithIcd(
                    term.replace(/fairly/gi, "").replace(/likely/gi, "")
                  )
                : []
            ] as [string, any[]]
        ).map(([term, codings]) =>
          codings.length > 0 ? `${term} [${codings[0].code}]` : term
        )).join("\n");
      }

      document[key] += (document[key].length === 0 ? "" : "\n") + text;

      resetSelection();
    };

    return {
      fontSize,
      scrollable,
      selection,
      range,
      itemsByToken,
      resetSelection,
      annotate
    };
  }
});
